import React, { useState } from "react";
import { CalendarClock, Sparkles } from "lucide-react";

/**
 * <CalDemoWidget /> — "Book a HustlePlate demo" band pinned to every
 * sub-sales page just above the back-to-overview CTA. Opens a Cal.com
 * pop-over (inline iframe) so warm readers can pick a slot without
 * bouncing back to the main funnel.
 *
 * The Cal.com slug is env-driven so ops can swap the account without a
 * redeploy: set REACT_APP_HUSTLEPLATE_CAL_URL in frontend/.env.
 * Default falls back to the hustleappz account's shared "hustleplate
 * demo" event type.
 */

const DEFAULT_CAL_URL =
  process.env.REACT_APP_HUSTLEPLATE_CAL_URL ||
  "https://demo.hustleplate.au";

export default function CalDemoWidget({
  variant = "band",
  testid = "hp-cal-demo",
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <section
        data-testid={testid}
        className="max-w-7xl mx-auto px-6 pt-6 pb-2"
      >
        <div
          className="relative overflow-hidden rounded-3xl border border-emerald-200
                     dark:border-emerald-800 bg-gradient-to-br
                     from-emerald-50 via-white to-amber-50
                     dark:from-emerald-950/40 dark:via-slate-950 dark:to-amber-950/30
                     px-6 py-8 md:px-10 md:py-10 shadow-xl"
        >
          <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center">
            <div>
              <div className="inline-flex items-center gap-1.5 text-[11px]
                              font-bold tracking-widest uppercase text-emerald-700
                              dark:text-emerald-300">
                <Sparkles className="h-3.5 w-3.5" />
                See it live · 20-minute personal walkthrough
              </div>
              <h3 className="mt-2 text-2xl md:text-3xl font-black
                             text-slate-900 dark:text-white leading-tight">
                Book a HustlePlate demo — we'll walk your venue through it.
              </h3>
              <p className="mt-3 text-base text-slate-700 dark:text-slate-300
                            max-w-2xl leading-relaxed">
                Pick a 20-minute slot that suits your service window. We share our
                screen, load your menu, and show exactly how the terminal, KDS,
                bookings and receptionist all click together. Zero pressure — walk
                away with a costed migration plan whether you sign or not.
              </p>
            </div>
            <div className="flex md:justify-end">
              <button
                type="button"
                onClick={() => setOpen(true)}
                data-testid={`${testid}-open`}
                className="inline-flex items-center gap-2 bg-emerald-600
                           hover:bg-emerald-500 text-white font-semibold
                           px-6 py-3.5 rounded-full shadow-lg
                           hover:shadow-xl transition text-base"
              >
                <CalendarClock className="h-5 w-5" />
                Pick my demo slot
              </button>
            </div>
          </div>
        </div>
      </section>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          data-testid={`${testid}-modal`}
          className="fixed inset-0 z-[60] bg-slate-950/70 backdrop-blur-sm
                     flex items-center justify-center p-3 md:p-6"
          onClick={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
        >
          <div
            className="relative w-full max-w-4xl h-[85vh] bg-white
                       dark:bg-slate-950 rounded-2xl shadow-2xl overflow-hidden
                       border border-slate-200 dark:border-slate-800"
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid={`${testid}-close`}
              aria-label="Close"
              className="absolute top-3 right-3 z-10 h-9 w-9 rounded-full
                         bg-white dark:bg-slate-900 border border-slate-200
                         dark:border-slate-700 shadow-md hover:shadow-lg
                         flex items-center justify-center text-slate-700
                         dark:text-slate-200 hover:bg-slate-50
                         dark:hover:bg-slate-800 transition"
            >
              ✕
            </button>
            <iframe
              src={DEFAULT_CAL_URL}
              title="Book a HustlePlate demo"
              className="w-full h-full border-0"
              allow="camera; microphone; autoplay"
            />
          </div>
        </div>
      )}
    </>
  );
}
